// src/pages/Home.js

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from "recharts";
import axios from "axios";

function ArtistChart({ artistName }) {
  const [albums, setAlbums] = useState([]);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    axios
      .get(`https://critica-backend.onrender.com/genre/rap`)
      .then((res) => {
        const artist = res.data.find(
          (a) => a.artist.toLowerCase() === artistName.toLowerCase()
        );
        if (!artist) return;

        const data = artist.albums
          .map((a) => ({
            album: a["Album"] || "Unknown Album",
            release_year: Number.parseInt(a["Release Year"], 10),
            pitchfork: Number.parseFloat(a["Pitchfork"]),
            metacritic: Number.parseFloat(a["Metacritic (/10)"]),
            fantano: Number.parseFloat(a["Anthony Fantano"]),
          }))
          .filter((a) => a.release_year);

        data.sort((a, b) => a.release_year - b.release_year);
        setAlbums(data);
      })
      .catch((err) => console.error("Error fetching genre data:", err));
  }, [artistName]);

  const years = albums.map((a) => a.release_year).filter(Boolean);
  const firstYear = years.length ? Math.min(...years) : null;
  const lastYear = years.length ? Math.max(...years) : null;

  return (
    <div
      style={{
        backgroundColor: "#1E1E1E",
        borderRadius: "16px",
        padding: "18px 18px 14px",

        width: "400px",
        maxWidth: "92vw",

        // ✅ CRITICAL FIX (prevents overlap)
        boxSizing: "border-box",

        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "start",
        boxShadow: "0px 10px 30px rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.06)",
        backgroundImage:
          "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",

        // helps keep shadows nice
        position: "relative",
        isolation: "isolate",
      }}
    >
      <h3
        style={{
          textAlign: "center",
          fontSize: "20px",
          margin: "2px 0 14px 0",
          letterSpacing: "0.2px",
        }}
      >
        {artistName}
      </h3>

      <div style={{ width: "100%", height: "240px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={albums}
            onMouseMove={(e) => {
              const point = e?.activePayload?.[0]?.payload;
              if (point) {
                setHovered((prev) => (prev?.album === point.album ? prev : point));
              }
            }}
            onMouseLeave={() => setHovered(null)}
            margin={{ top: 10, right: 14, bottom: 28, left: 6 }}
          >
            <XAxis
              dataKey="release_year"
              stroke="#EAEAEA"
              tick={{ fontSize: 12, fontFamily: "Roboto, sans-serif" }}
              interval={0}
              tickMargin={8}
              tickFormatter={(value) => {
                if (value === firstYear || value === lastYear) return String(value);
                return "";
              }}
            />
            <YAxis
              domain={[0, 10]}
              ticks={[0, 10]}
              stroke="#EAEAEA"
              width={26}
              tick={{ fontSize: 12, fontFamily: "Roboto, sans-serif" }}
            />
            <Line type="monotone" dataKey="pitchfork" stroke="#057EFC" strokeWidth={3.45} dot={false} />
            <Line type="monotone" dataKey="metacritic" stroke="#04CD46" strokeWidth={3.45} dot={false} />
            <Line type="monotone" dataKey="fantano" stroke="#FCDD0C" strokeWidth={3.45} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {hovered ? (
        <div
          style={{
            marginTop: "14px",
            paddingBottom: "6px",
            fontSize: "13px",
            textAlign: "center",
            fontFamily: "Roboto, sans-serif",
            lineHeight: 1.35,
          }}
        >
          <p style={{ margin: 0, fontWeight: 700 }}>
            {hovered.album} ({hovered.release_year})
          </p>
          <p style={{ margin: "8px 0 0", color: "#057EFC" }}>
            Pitchfork: {hovered.pitchfork}
          </p>
          <p style={{ margin: "2px 0 0", color: "#04CD46" }}>
            Metacritic: {hovered.metacritic}
          </p>
          <p style={{ margin: "2px 0 0", color: "#FCDD0C" }}>
            Fantano: {hovered.fantano}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Home() {
  const [showDropdown, setShowDropdown] = useState(false);

  const linkStyle = {
    display: "block",
    padding: "10px 20px",
    textDecoration: "none",
    backgroundColor: "transparent",
    color: "white",
    fontSize: "16px",
    width: "100%",
    textAlign: "left",
    fontFamily: "'Rubik', sans-serif",
    whiteSpace: "nowrap",
  };

  return (
    <div
      style={{
        backgroundColor: "#121212",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        color: "white",
        fontFamily: "'Rubik', sans-serif",
        paddingBottom: "60px",
      }}
    >
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Rubik&display=swap" rel="stylesheet" />
        <link href="https://fonts.googleapis.com/css2?family=Roboto&display=swap" rel="stylesheet" />
      </Helmet>

      <header
        style={{
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          padding: "18px 22px",
          borderBottom: "2px solid #1E1E1E",
        }}
      >
        <h1
          style={{
            fontSize: "45px",
            fontWeight: "bold",
            color: "#04CD46",
            fontFamily: "'Rubik', sans-serif",
            textAlign: "left",
            textTransform: "lowercase",
            margin: 0,
          }}
        >
          critica
        </h1>

        <nav
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "18px",
            position: "relative",
            fontFamily: "'Rubik', sans-serif",
          }}
        >
          <Link to="/" style={{ textDecoration: "none", color: "white", fontSize: "18px", fontWeight: "bold" }}>
            HOME
          </Link>

          <div
            style={{ position: "relative", display: "inline-block" }}
            onMouseEnter={() => setShowDropdown(true)}
            onMouseLeave={() => setShowDropdown(false)}
          >
            <span
              role="button"
              tabIndex={0}
              style={{
                textDecoration: "none",
                color: "white",
                fontSize: "18px",
                fontWeight: "bold",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => setShowDropdown((prev) => !prev)}
            >
              CHARTS ▼
            </span>

            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "110%",
                  left: "0",
                  backgroundColor: "#1E1E1E",
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0px 10px 24px rgba(0,0,0,0.45)",
                  zIndex: "100",
                  minWidth: "140px",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                {["rap", "pop"].map((genre) => (
                  <Link
                    key={genre}
                    to={`/charts/${genre}`}
                    style={linkStyle}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = "#2A2A2A")}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                  >
                    {genre.charAt(0).toUpperCase() + genre.slice(1)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/compare" style={{ textDecoration: "none", color: "white", fontSize: "18px", fontWeight: "bold" }}>
            COMPARE
          </Link>
        </nav>

        <div />
      </header>

      <div style={{ width: "100%", maxWidth: "1100px", padding: "0 22px" }}>
        <div style={{ textAlign: "center", marginTop: "42px" }}>
          <h1 style={{ fontSize: "40px", fontWeight: "bold", marginBottom: "16px" }}>
            Discover Critic Ratings Like Never Before
          </h1>
          <p style={{ fontSize: "18px", lineHeight: "1.7", margin: "0 auto", maxWidth: "820px" }}>
            Explore critic scores from <strong>Pitchfork, Metacritic, and Fantano</strong> across your
            favorite artists. Use <strong>Critica</strong> to track{" "}
            <strong>trends, compare artists, and dive into rating history.</strong>
          </p>
        </div>

        {/* ✅ close together, no overlap */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, 400px)",
            gap: "30px",
            marginTop: "54px",
            paddingBottom: "60px",
            justifyContent: "center",
            justifyItems: "center",
            alignItems: "start",
          }}
        >
          <ArtistChart artistName="Drake" />
          <ArtistChart artistName="Kendrick Lamar" />
        </div>
      </div>
    </div>
  );
}

export default Home;
